import { useState, useEffect } from 'react';
import { toUtcISOString, localInputToUtc } from 'common/utils/datetime';
import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { FlashRepository } from 'repository/flashRepository';
import { useAlert } from 'contexts/AlertContext';
import { getFlashCTACopy } from 'common/utils/flashCta';
import { FlashFrequentCoMembers } from 'components/flash/FlashFrequentCoMembers';
import { markOnboardingCompleted } from 'common/utils/onboarding';
import {
    AppScreen, Button, ButtonFooter, FieldGroup, Section, SectionHeader, TextField,
} from 'components/ds';
import { IcClose } from 'components/common/icons/EmojiIcons';
import styles from './Flash.module.css';

/**
 * 급번개 생성 (시간/제목/마감 설정)
 * - PlaceExplore에서 다중 선택 후 이 화면으로 state를 전달받음
 * - location.state.flashCandidates: 선택된 후보 배열
 */

interface CandidateInput {
    placeName: string;
    placeAddress?: string;
    latitude?: number;
    longitude?: number;
    externalPlaceId?: string;
    imageUrl?: string;
}

const pad = (n: number) => String(n).padStart(2, '0');
const toLocalDateTimeString = (d: Date) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

export const FlashCreate = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { showAlert } = useAlert();
    const { t } = useTranslation();

    const initialCandidates: CandidateInput[] = location.state?.flashCandidates || [];
    const [candidates, setCandidates] = useState<CandidateInput[]>(initialCandidates);
    // 시간대별 재미있는 카피 — 홈 배너와 같은 풀에서 picker. 마운트 시점 1회 고정 (재렌더마다 바뀌면 어색).
    const [flashCopy] = useState(() => getFlashCTACopy());
    const [title, setTitle] = useState('');  // 빈값이면 제출 시 flashCopy.title 적용

    // 기본 시작: 현재 + 2시간 (반올림)
    const defaultStart = (() => {
        const d = new Date();
        d.setHours(d.getHours() + 2, 0, 0, 0);
        return toLocalDateTimeString(d);
    })();
    const defaultEnd = (() => {
        const d = new Date();
        d.setHours(d.getHours() + 4, 0, 0, 0);
        return toLocalDateTimeString(d);
    })();

    const [startAt, setStartAt] = useState(defaultStart);
    const [endAt, setEndAt] = useState(defaultEnd);
    const [deadlineMinutes, setDeadlineMinutes] = useState(60);
    const [memo, setMemo] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    // 선택된 자주 함께한 사람 — 번개 생성 직후 일괄 초대 push
    const [selectedInviteeNos, setSelectedInviteeNos] = useState<number[]>([]);

    useEffect(() => {
        if (initialCandidates.length < 1) {
            showAlert({ message: t('flash.create.minCandidates') });
            navigate('/meeting/create/place-explore?mode=flash', { replace: true });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const removeCandidate = (idx: number) => {
        setCandidates(prev => prev.filter((_, i) => i !== idx));
    };

    const handleCreate = async () => {
        if (candidates.length < 1) {
            showAlert({ message: t('flash.create.noCandidates') });
            return;
        }
        if (!startAt || !endAt) {
            showAlert({ message: t('flash.create.noTime') });
            return;
        }
        setIsCreating(true);
        try {
            // 서버는 시각을 UTC 로 받습니다
            const deadlineIso = toUtcISOString(new Date(Date.now() + deadlineMinutes * 60 * 1000));
            const payload = {
                title: title.trim() || flashCopy.title,
                memo: memo.trim() || undefined,
                startDatetime: localInputToUtc(startAt),
                endDatetime: localInputToUtc(endAt),
                voteDeadline: deadlineIso,
                candidates: candidates.map(c => ({
                    placeName: c.placeName,
                    placeAddress: c.placeAddress,
                    latitude: c.latitude,
                    longitude: c.longitude,
                    externalPlaceId: c.externalPlaceId,
                    imageUrl: c.imageUrl,
                })),
            };
            const res = await FlashRepository.onPostCreate(payload);
            const flash = res?.data?.data || res?.data;
            if (!flash?.shareToken) throw new Error('failed');
            // 선택된 사람들에게 초대 푸시 일괄 발사 (실패해도 생성 흐름은 그대로 진행)
            if (selectedInviteeNos.length > 0 && flash.flashId) {
                try {
                    await FlashRepository.onPostInvite(flash.flashId, { inviteeUserNos: selectedInviteeNos });
                    markOnboardingCompleted('step_invite');
                } catch { /* 초대 실패는 silent — share 화면에서 다시 초대 가능 */ }
            }
            navigate(`/flash/share/${flash.shareToken}`, {
                replace: true,
                state: { invitedUserNos: selectedInviteeNos },
            });
        } catch (e: any) {
            showAlert({ message: e?.response?.data?.message || t('flash.create.createFailed') });
        } finally {
            setIsCreating(false);
        }
    };

    const deadlineLabels: Record<number, string> = {
        30: t('flash.create.deadline30'),
        60: t('flash.create.deadline60'),
        120: t('flash.create.deadline120'),
        180: t('flash.create.deadline180'),
    };

    /** 자주 쓰는 시간대 — 누르면 시작/끝을 한 번에 채웁니다 */
    const quickTimes = [
        {
            label: t('flash.create.quickHour1'),
            make: () => { const s = new Date(); s.setMinutes(0, 0, 0); s.setHours(s.getHours() + 1); return s; },
        },
        {
            label: t('flash.create.quickHour2'),
            make: () => { const s = new Date(); s.setMinutes(0, 0, 0); s.setHours(s.getHours() + 2); return s; },
        },
        {
            label: t('flash.create.quickEvening'),
            make: () => { const s = new Date(); s.setHours(19, 0, 0, 0); return s; },
        },
    ];

    const applyQuick = (make: () => Date) => {
        const s = make();
        const e = new Date(s);
        e.setHours(e.getHours() + 2);
        setStartAt(toLocalDateTimeString(s));
        setEndAt(toLocalDateTimeString(e));
    };

    return (
        <AppScreen title={t('flash.header.create')} bar="sub" hasFooter>
            {/* 후보 장소 */}
            <Section spacing="tight">
                <SectionHeader
                    title={t('flash.create.candidatesLabel', { count: candidates.length })}
                />
                <ul className={styles.cands}>
                    {candidates.map((c, idx) => (
                        <li key={idx} className={styles.cand}>
                            <span className={styles.candName}>{c.placeName}</span>
                            <button
                                type="button"
                                className={styles.candRemove}
                                onClick={() => removeCandidate(idx)}
                                aria-label={t('common.delete')}
                            >
                                <IcClose size={16} />
                            </button>
                        </li>
                    ))}
                </ul>
                <div className={styles.addAction}>
                    <Button
                        variant="secondary"
                        size="lg"
                        block
                        onClick={() => navigate('/meeting/create/place-explore?mode=flash', {
                            state: { flashCandidates: candidates },
                        })}
                    >
                        {t('flash.create.addMore')}
                    </Button>
                </div>
            </Section>

            {/* 이름 · 메모 */}
            <Section divided>
                <FieldGroup>
                    <TextField
                        id="flash-title"
                        label={t('flash.create.titleLabel')}
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder={flashCopy.title}
                        hint={flashCopy.sub || undefined}
                        maxLength={50}
                    />
                    <TextField
                        id="flash-memo"
                        label={t('flash.create.memoLabel')}
                        type="text"
                        value={memo}
                        onChange={(e) => setMemo(e.target.value)}
                        placeholder={t('flash.create.memoPlaceholder')}
                        maxLength={200}
                    />
                </FieldGroup>
            </Section>

            {/* 언제 */}
            <Section divided>
                <SectionHeader title={t('flash.create.whenLabel')} />
                <div className={styles.chips}>
                    {quickTimes.map(q => (
                        <button key={q.label} type="button" className={styles.chip} onClick={() => applyQuick(q.make)}>
                            {q.label}
                        </button>
                    ))}
                </div>
                <div className={styles.timeRow}>
                    <input
                        type="datetime-local"
                        className={styles.time}
                        value={startAt}
                        onChange={(e) => setStartAt(e.target.value)}
                        aria-label={t('flash.create.whenLabel')}
                    />
                    <span className={styles.timeSep} aria-hidden>→</span>
                    <input
                        type="datetime-local"
                        className={styles.time}
                        value={endAt}
                        onChange={(e) => setEndAt(e.target.value)}
                        aria-label={t('flash.create.whenLabel')}
                    />
                </div>
            </Section>

            {/* 투표 마감 */}
            <Section divided>
                <SectionHeader title={t('flash.create.deadlineLabel')} />
                <div className={styles.chips}>
                    {[30, 60, 120, 180].map(min => (
                        <button
                            key={min}
                            type="button"
                            className={deadlineMinutes === min ? `${styles.chip} ${styles.chipOn}` : styles.chip}
                            onClick={() => setDeadlineMinutes(min)}
                            aria-pressed={deadlineMinutes === min}
                        >
                            {deadlineLabels[min]}
                        </button>
                    ))}
                </div>
            </Section>

            {/* 자주 함께한 사람 — 고른 사람에게 만든 직후 초대를 보냅니다 */}
            <Section divided last>
                <FlashFrequentCoMembers
                    selectedUserNos={selectedInviteeNos}
                    onSelectionChange={setSelectedInviteeNos}
                />
            </Section>

            <ButtonFooter>
                <Button variant="ghost" size="lg" onClick={() => navigate(-1)}>
                    {t('flash.create.cancel')}
                </Button>
                <Button
                    variant="primary"
                    size="lg"
                    block
                    onClick={handleCreate}
                    disabled={isCreating || candidates.length < 1}
                >
                    {isCreating ? t('flash.create.submitting') : t('flash.create.submit')}
                </Button>
            </ButtonFooter>
        </AppScreen>
    );
};
